import { gql } from '@apollo/client';
import { baseChipFragment, renderFragments, resellerChipFragment } from '../../lib/fragments';

/**
 * Query to check if a chip is accessible to the current reseller.
 * Returns the chip if the user is the reseller of the chip OR owns it.
 * Throws Forbidden if the chip belongs to another reseller and the user doesn't own it.
 */
export const checkResellerChipAccessQuery = gql`
  query NANO_checkResellerChipAccess($id: ID!) {
    resellerChip(id: $id) {
      id
      serialNumber
      pet {
        id
        name
        species
        gender
        breed {
          id
          name
        }
        isPurebred
        color
        yearOfBirth
        monthOfBirth
        dayOfBirth
        weight
      }
      reseller {
        id
      }
      latestContactHistoryEntry {
        contactInfo {
          firstName
          lastName
          primaryEmail
          primaryPhone
        }
      }
    }
    currentUser {
      chipReseller {
        id
      }
    }
  }
`;

export const chipRegistrationAddEmailMutation = gql`
  mutation NANO_chipRegistrationAddEmail($input: ChipRegistrationAddEmailInput!) {
    chipRegistrationAddEmail(input: $input) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

export const chipRegistrationAddNewPetMutation = gql`
  mutation NANO_chipRegistrationAddNewPet($input: ChipRegistrationAddNewPetInput!) {
    chipRegistrationAddNewPet(input: $input) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

export const chipRegistrationAddExistingPetMutation = gql`
  mutation NANO_chipRegistrationAddExistingPet($input: ChipRegistrationAddExistingPetInput!) {
    chipRegistrationAddExistingPet(input: $input) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

export const chipRegistrationUpdateContactMutation = gql`
  mutation NANO_chipRegistrationUpdateContact($input: ChipRegistrationUpdateContactInput!) {
    chipRegistrationUpdateContact(input: $input) {
      ...baseChip
    }
  }

  ${renderFragments(baseChipFragment)}
`;

export const assignNewPetToChipMutation = gql`
  mutation NANO_assignNewPetToChip($input: AssignNewPetToChipInput!) {
    assignNewPetToChip(input: $input) {
      ...resellerChip
    }
  }

  ${renderFragments(resellerChipFragment)}
`;

export const assignExistingPetToChipMutation = gql`
  mutation NANO_assignExistingPetToChip($input: AssignExistingPetToChipInput!) {
    assignExistingPetToChip(input: $input) {
      ...resellerChip
    }
  }

  ${renderFragments(resellerChipFragment)}
`;

export const assignNewOwnerToChipMutation = gql`
  mutation NANO_assignNewOwnerToChip($input: AssignNewOwnerToChipInput!) {
    assignNewOwnerToChip(input: $input) {
      ...resellerChip
    }
  }

  ${renderFragments(resellerChipFragment)}
`;

/**
 * Whether the pet behind a just-registered chip still wears a rescue's collar nobody has resolved.
 * Hand-typed below rather than generated, as `yarn gql:types` can't run on Node 24.
 */
export const rescueCollarStatusQuery = gql`
  query NANO_rescueCollarStatus($petId: ID!) {
    pet(id: $petId) {
      id
      hasUnresolvedRescueCollar
    }
  }
`;

export interface RescueCollarStatusData {
  pet: {
    id: string;
    hasUnresolvedRescueCollar: boolean;
  } | null;
}

export interface RescueCollarStatusVariables {
  petId: string;
}

/**
 * The adopter's answer to "did your rescue send you home with a Fi Mini?". Yes opens a pending
 * adoption transfer for the collar; No returns it to the rescue. Both are refused unless Nano
 * registration deferred the collar for this registrant. Hand-typed, as above.
 */
export const adopterClaimRescueCollarMutation = gql`
  mutation NANO_adopterClaimRescueCollar($input: AdopterClaimRescueCollarInput!) {
    adopterClaimRescueCollar(input: $input) {
      transfer {
        id
      }
    }
  }
`;

export const adopterReturnRescueCollarMutation = gql`
  mutation NANO_adopterReturnRescueCollar($input: AdopterReturnRescueCollarInput!) {
    adopterReturnRescueCollar(input: $input) {
      pet {
        id
      }
    }
  }
`;

export interface AdopterClaimRescueCollarData {
  adopterClaimRescueCollar: {
    transfer: {
      id: string;
    };
  };
}

export interface AdopterReturnRescueCollarData {
  adopterReturnRescueCollar: {
    pet: {
      id: string;
    };
  };
}

export interface AdopterRescueCollarVariables {
  input: {
    petId: string;
  };
}
